require('./index.scss')

import debounce from 'debounce'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import Dialog from '@/components/Dialog'
import { update } from '@/reducers/lineItems/asyncActions'
import { isBulkOrBulkIntegrationEnabledSelector } from '@/selectors'
import { localePrice } from '@/utils/priceLocalization'
import { handleQuantityChange } from '@/utils/quantityValidator'

import Item from './components/Item'
import QuantityInput from './components/QuantityInput'
import Trigger from './components/Trigger'
import VariantSelector from './components/VariantSelector'

const cartItemModifierClass = [
  'cart-item-edit__item-modifier',
  'd-flex',
  'align-items-baseline',
  'u-margin-top--2'
].join(' ')

const cartSubTotalClass = [
  'cart-item-edit__item-price',
  'line-height-3',
  'qa-item-subtotal',
  'u-align--right',
  'u-weight--bold'
].join(' ')

const DEBOUNCE_DELAY = 200

const CartItemEditModal = ({ id }) => {
  const lineItem = useSelector(state => state.lineItems.items[id])
  const { asset, quantity, variant, variantOptions } = lineItem
  const { title } = asset

  const dispatch = useDispatch()
  const intl = useIntl()
  const editText = intl.formatMessage({ id: 'common.edit' })

  const isBulkOrBulkIntegrationEnabled = useSelector(
    isBulkOrBulkIntegrationEnabledSelector
  )
  const isEditingAllowed = isBulkOrBulkIntegrationEnabled
    || variantOptions.length > 1

  const [isShowing, setIsShowing] = useState(false)
  const [selectedQuantity, setSelectedQuantity] = useState(quantity)
  const [selectedVariant, setSelectedVariant] = useState(variant)
  const selectedStudentPrice = selectedVariant.studentPrice
  const [studentPrice, setStudentPrice] = useState(selectedStudentPrice)
  const [totalPrice, setTotalPrice] = useState(
    selectedStudentPrice * selectedQuantity
  )

  const handleUpdateVariant = () => {
    dispatch(update({
      id,
      quantity: selectedQuantity,
      variantId: selectedVariant.id
    }))
    setIsShowing(false)
  }

  const handleUpdateQuantity = event => handleQuantityChange(
    event, setSelectedQuantity
  )
  const debounceHandleUpdateQuantity = debounce(
    handleUpdateQuantity, DEBOUNCE_DELAY
  )
  const price = localePrice(studentPrice, { hideCurrencyLabel: true })

  useEffect(() => {
    setStudentPrice(selectedStudentPrice)
    setTotalPrice(selectedStudentPrice * selectedQuantity)
  }, [selectedQuantity, selectedVariant])

  return (
    <If condition={isEditingAllowed}>
      <div className='cart-item__edit'>
        <Dialog
          heading={editText}
          id='cart-item-edit-modal'
          isShowing={isShowing}
          onChangeShow={setIsShowing}
          trigger={<Trigger ariaLabel={`${editText} '${title}'`} />}
        >
          <div className='cart-item-edit'>
            <div className='d-flex flex-row justify-content-between u-gap'>
              <Item asset={asset} price={price} variant={selectedVariant} />
              <div
                className={cartSubTotalClass}
                role='status'
              >
                {localePrice(totalPrice, { hideCurrencyLabel: true })}
              </div>
            </div>

            <div className={cartItemModifierClass}>
              <QuantityInput
                handleUpdateQuantity={debounceHandleUpdateQuantity}
                id={id}
                quantity={selectedQuantity}
              />
              <VariantSelector
                {...{
                  id,
                  setSelectedVariant
                }}
              />
            </div>
          </div>

          <div className='u-margin-top--3 u-align--right'>
            <button
              className='vst-btn--transparent u-push--right'
              onClick={() => setIsShowing(false)}
            >
              <FormattedMessage id='common.cancel' />
            </button>

            <button
              className='vst-btn-modal vst-btn--branded'
              onClick={handleUpdateVariant}
            >
              <FormattedMessage id='common.save' />
            </button>
          </div>
        </Dialog>
      </div>
    </If>
  )
}

CartItemEditModal.propTypes = { id: PropTypes.number.isRequired }

export default CartItemEditModal
